import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  KeyRound,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  Shield,
  Stethoscope,
  Users as UsersIcon,
} from 'lucide-react';
import {
  AdminSetPasswordSchema,
  CreateUserSchema,
  roleLabelFor,
  type UpdateUserInput,
  type User,
  type UserStatus,
} from '@datazo/contracts';
import { useRoles } from '@/features/roles/api';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ApiClientError } from '@/shared/api/client';
import { useAuthStore } from '@/shared/store/auth';
import { useToast } from '@/shared/toast/toast-context';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { avatarGradient } from '@/components/ui/avatar';
import { usePageTitle } from '@/shared/store/page';
import { useUsersList, useCreateUser, useUpdateUser, useSetUserPassword } from './api';

// El directorio es pequeño (~30 personas): traemos todo en una página y
// particionamos en cliente entre "Cuentas de acceso" y "Personal clínico".
// 100 es el máximo permitido por PaginationQuerySchema.
const DEFAULT_PAGE_SIZE = 100;

type TabKey = 'acceso' | 'clinico';

/** Helper local: divide nombre completo y devuelve sus iniciales. */
function initialsFromFullName(full: string): string {
  const parts = full
    .replace(/^(Dr\.|Dra\.|Enf\.)\s+/i, '')
    .trim()
    .split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return (first + last).toUpperCase();
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

function formatIngreso(iso: string | undefined): string {
  if (iso === undefined) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('es-CO', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function UsersListPage(): JSX.Element {
  usePageTitle('Usuarios y personal', 'Cuentas de acceso y directorio clínico');
  const isAdmin = useAuthStore((s) => s.user?.rol === 'admin');
  // Solo un súper admin puede editar/resetear la contraseña de otro súper admin.
  const isSuperAdmin = useAuthStore((s) => s.user?.esSuperAdmin === true);
  const rolesQuery = useRoles();
  const roleNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rolesQuery.data ?? []) m.set(r.id, r.nombre);
    return m;
  }, [rolesQuery.data]);
  const displayRole = (u: User): string =>
    u.esSuperAdmin === true ? 'Súper Administrador' : (roleNameById.get(u.rol) ?? roleLabelFor(u));
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const [tab, setTab] = useState<TabKey>('acceso');
  const [showNew, setShowNew] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<User | null>(null);

  const { data, isLoading, isFetching, isError, error, refetch } = useUsersList({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    q: debouncedQuery,
  });

  const items = data?.items ?? [];

  // Partición: cuentas de acceso (pueden iniciar sesión) vs personal de
  // directorio (médicos, anestesiólogos y enfermeras — no inician sesión).
  const { acceso, clinico } = useMemo(() => {
    const acc: User[] = [];
    const cli: User[] = [];
    for (const u of items) {
      if (u.puedeIniciarSesion === true) acc.push(u);
      else cli.push(u);
    }
    return { acceso: acc, clinico: cli };
  }, [items]);

  const visible = tab === 'acceso' ? acceso : clinico;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="relative min-w-[320px] flex-1 max-w-[480px]">
          <Input
            placeholder="Buscar por nombre, correo o rol…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1.5 text-text-muted text-xs">
          <Shield className="w-3.5 h-3.5" /> Solo administradores pueden modificar
        </div>
        {isAdmin && (
          <Button className="ml-auto" onClick={() => setShowNew(true)}>
            <Plus className="w-4 h-4" /> Nuevo usuario
          </Button>
        )}
      </div>

      {/* Pestañas */}
      <div className="flex items-center gap-1 p-1 rounded-lg bg-surface-alt w-fit">
        <TabButton
          active={tab === 'acceso'}
          onClick={() => setTab('acceso')}
          icon={<KeyRound className="w-4 h-4" />}
          label="Cuentas de acceso"
          count={acceso.length}
        />
        <TabButton
          active={tab === 'clinico'}
          onClick={() => setTab('clinico')}
          icon={<Stethoscope className="w-4 h-4" />}
          label="Personal clínico"
          count={clinico.length}
        />
      </div>

      {isError && (
        <div className="bg-danger-soft border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">No fue posible cargar los usuarios</div>
            <div className="text-sm text-red-700">{error.message}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            <RefreshCw className="w-4 h-4" /> Reintentar
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>
              {tab === 'acceso' ? 'Cuentas de acceso' : 'Personal clínico (directorio)'}
            </CardTitle>
            <CardDescription>
              {isLoading
                ? 'Cargando…'
                : tab === 'acceso'
                  ? `${acceso.length} ${acceso.length === 1 ? 'cuenta que inicia sesión' : 'cuentas que inician sesión'} (admin + enfermería compartida)`
                  : `${clinico.length} ${clinico.length === 1 ? 'persona' : 'personas'} del directorio — firman documentos pero no inician sesión`}
              {isFetching && !isLoading ? ' · actualizando…' : ''}
            </CardDescription>
          </div>
        </CardHeader>

        {isLoading ? (
          <ListSkeleton />
        ) : !isError && visible.length === 0 ? (
          <EmptyState hasFilters={debouncedQuery !== ''} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Especialidad / Registro</TableHead>
                <TableHead>{tab === 'acceso' ? 'Último ingreso' : 'Acceso'}</TableHead>
                {isAdmin && <TableHead className="w-[90px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody className="stagger-children">
              {visible.map((u) => (
                <TableRow
                  key={u.id}
                  className="animate-fade-in-up hover:bg-surface-alt transition-colors"
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-full grid place-items-center text-white text-xs font-semibold ${avatarGradient(u.id)}`}
                      >
                        {initialsFromFullName(u.nombre)}
                      </div>
                      <div>
                        <div className="font-medium text-text">{u.nombre}</div>
                        <div className="text-text-muted text-xs">{u.correo}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium">{displayRole(u)}</span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        u.estado === 'activo'
                          ? 'green'
                          : u.estado === 'inactivo'
                            ? 'gray'
                            : 'red'
                      }
                    >
                      {u.estado}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{u.especialidad ?? '—'}</div>
                    {u.registroProfesional !== undefined && (
                      <div className="text-text-muted text-xs">{u.registroProfesional}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {tab === 'acceso' ? (
                      <span className="text-text-muted text-xs">
                        {formatIngreso(u.ultimoIngreso)}
                      </span>
                    ) : (
                      <Badge variant="gray">Sin acceso</Badge>
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {u.esSuperAdmin && !isSuperAdmin ? (
                        <span
                          className="inline-flex items-center gap-1 text-text-muted text-xs"
                          title="Solo un súper administrador puede modificar esta cuenta"
                        >
                          <Lock className="w-3.5 h-3.5" /> Protegido
                        </span>
                      ) : (
                        <Button variant="secondary" size="sm" onClick={() => setEditUser(u)}>
                          <Pencil className="w-3.5 h-3.5" /> Editar
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {showNew && (
        <NewUserModal
          defaultDirectory={tab === 'clinico'}
          onClose={() => setShowNew(false)}
        />
      )}

      {editUser !== null && (
        <EditUserModal user={editUser} onClose={() => setEditUser(null)} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: JSX.Element;
  label: string;
  count: number;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium transition-colors ${
        active
          ? 'bg-surface text-text shadow-sm'
          : 'text-text-muted hover:text-text'
      }`}
    >
      {icon}
      {label}
      <span
        className={`inline-grid place-items-center min-w-[20px] h-5 px-1 rounded-full text-[11px] ${
          active ? 'bg-primary/10 text-primary' : 'bg-border text-text-muted'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function ListSkeleton(): JSX.Element {
  return (
    <div className="p-6 flex flex-col gap-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-12 rounded-md bg-surface-alt animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }): JSX.Element {
  return (
    <div className="p-10 text-center flex flex-col items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-surface-alt grid place-items-center text-text-muted">
        <UsersIcon className="w-6 h-6" />
      </div>
      <div className="text-text font-semibold">
        {hasFilters ? 'Sin coincidencias' : 'No hay usuarios registrados'}
      </div>
      <p className="text-text-muted text-sm max-w-sm">
        {hasFilters
          ? 'Ajusta el término de búsqueda para ver resultados.'
          : 'La administración de cuentas se realiza desde este módulo.'}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Modal: crear usuario / personal                                            */
/* -------------------------------------------------------------------------- */

/**
 * Por defecto, el personal de directorio clínico (médicos, anestesiólogos,
 * enfermeras individuales) NO inicia sesión; el resto de perfiles (admin,
 * recepción, auditoría y los personalizados) sí.
 */
function loginDefaultForRole(rol: string): boolean {
  return !['medico', 'anestesiologo', 'enfermera'].includes(rol);
}

function NewUserModal({
  defaultDirectory,
  onClose,
}: {
  defaultDirectory: boolean;
  onClose: () => void;
}): JSX.Element {
  const createUser = useCreateUser();
  const { showToast } = useToast();
  const roleList = useRoles().data ?? [];

  const [rol, setRol] = useState<string>(defaultDirectory ? 'medico' : 'recepcion');
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [estado, setEstado] = useState<UserStatus>('activo');
  const [especialidad, setEspecialidad] = useState('');
  const [registroProfesional, setRegistro] = useState('');
  const [cedula, setCedula] = useState('');
  const [password, setPassword] = useState('');
  const [puedeIniciarSesion, setPuedeIniciar] = useState<boolean>(
    !defaultDirectory,
  );
  const [error, setError] = useState<string | null>(null);

  const handleRol = (next: string): void => {
    setRol(next);
    setPuedeIniciar(loginDefaultForRole(next));
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const candidate = {
      nombre: nombre.trim(),
      correo: correo.trim(),
      rol,
      estado,
      password,
      puedeIniciarSesion,
      ...(especialidad.trim() === '' ? {} : { especialidad: especialidad.trim() }),
      ...(registroProfesional.trim() === ''
        ? {}
        : { registroProfesional: registroProfesional.trim() }),
      ...(cedula.trim() === '' ? {} : { cedula: cedula.trim() }),
    };
    const parsed = CreateUserSchema.safeParse(candidate);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setError(first?.message ?? 'Datos inválidos.');
      return;
    }
    try {
      await createUser.mutateAsync(parsed.data);
      showToast({
        kind: 'success',
        title: 'Usuario creado',
        description: puedeIniciarSesion
          ? 'La cuenta puede iniciar sesión con la contraseña temporal.'
          : 'Registrado en el directorio (sin acceso al sistema).',
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'No fue posible crear el usuario.',
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-bold text-text">Nuevo usuario / personal</h3>
          <button
            type="button"
            className="text-text-muted hover:text-text text-xl leading-none"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <form onSubmit={(e) => void submit(e)} className="p-5 flex flex-col gap-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="nu-nombre">
                Nombre completo <span className="text-danger">*</span>
              </Label>
              <Input
                id="nu-nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Carolina Mejía Pérez"
                required
              />
            </div>
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="nu-correo">
                Correo institucional <span className="text-danger">*</span>
              </Label>
              <Input
                id="nu-correo"
                type="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                placeholder="nombre.apellido@endomedical.co"
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nu-rol">Rol</Label>
              <select
                id="nu-rol"
                value={rol}
                onChange={(e) => handleRol(e.target.value)}
                className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {roleList.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nu-estado">Estado</Label>
              <select
                id="nu-estado"
                value={estado}
                onChange={(e) => setEstado(e.target.value as UserStatus)}
                className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="activo">activo</option>
                <option value="inactivo">inactivo</option>
                <option value="bloqueado">bloqueado</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nu-esp">Especialidad</Label>
              <Input
                id="nu-esp"
                value={especialidad}
                onChange={(e) => setEspecialidad(e.target.value)}
                placeholder="Gastroenterólogo / Enfermería…"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nu-rm">Registro profesional</Label>
              <Input
                id="nu-rm"
                value={registroProfesional}
                onChange={(e) => setRegistro(e.target.value)}
                placeholder="R.M. 12345"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nu-cc">Cédula</Label>
              <Input
                id="nu-cc"
                value={cedula}
                onChange={(e) => setCedula(e.target.value)}
                placeholder="1140xxxxxx"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nu-pwd">Contraseña temporal</Label>
              <Input
                id="nu-pwd"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 12: mayús, minús, dígito y especial"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-text-soft cursor-pointer select-none mt-1">
            <input
              type="checkbox"
              checked={puedeIniciarSesion}
              onChange={(e) => setPuedeIniciar(e.target.checked)}
              className="rounded border-border text-primary focus:ring-primary/20"
            />
            Puede iniciar sesión (cuenta de acceso)
          </label>
          <p className="text-[11px] text-text-muted -mt-1">
            Médicos, anestesiólogos y enfermeras del directorio normalmente
            <strong> no</strong> inician sesión. Las enfermeras de payroll se
            seleccionan del directorio al firmar y marcar turno.
          </p>

          {error !== null && (
            <div className="rounded-md bg-danger-soft border border-red-200 text-red-800 text-xs px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending ? 'Creando…' : 'Crear usuario'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Modal: editar usuario + cambiar contraseña                                 */
/* -------------------------------------------------------------------------- */

function EditUserModal({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}): JSX.Element {
  const updateUser = useUpdateUser();
  const setPassword = useSetUserPassword();
  const { showToast } = useToast();
  const roleList = useRoles().data ?? [];

  const [nombre, setNombre] = useState(user.nombre);
  const [correo, setCorreo] = useState(user.correo);
  const [rol, setRol] = useState<string>(user.rol);
  const [estado, setEstado] = useState<UserStatus>(user.estado);
  const [especialidad, setEspecialidad] = useState(user.especialidad ?? '');
  const [registroProfesional, setRegistro] = useState(user.registroProfesional ?? '');
  const [cedula, setCedula] = useState(user.cedula ?? '');
  const [puedeIniciarSesion, setPuedeIniciar] = useState<boolean>(
    user.puedeIniciarSesion === true,
  );
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    // 1. Validar contraseña si se va a cambiar.
    const wantsPassword = newPassword.trim() !== '';
    if (wantsPassword) {
      const pw = AdminSetPasswordSchema.safeParse({ password: newPassword });
      if (!pw.success) {
        setError(pw.error.issues[0]?.message ?? 'Contraseña inválida.');
        return;
      }
    }

    const patch: UpdateUserInput = {
      nombre: nombre.trim(),
      correo: correo.trim(),
      rol,
      estado,
      puedeIniciarSesion,
      ...(especialidad.trim() === '' ? {} : { especialidad: especialidad.trim() }),
      ...(registroProfesional.trim() === ''
        ? {}
        : { registroProfesional: registroProfesional.trim() }),
      ...(cedula.trim() === '' ? {} : { cedula: cedula.trim() }),
    };

    try {
      await updateUser.mutateAsync({ id: user.id, input: patch });
      if (wantsPassword) {
        await setPassword.mutateAsync({ id: user.id, input: { password: newPassword } });
      }
      showToast({
        kind: 'success',
        title: 'Usuario actualizado',
        description: wantsPassword ? 'Datos y contraseña guardados.' : 'Cambios guardados.',
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'No fue posible guardar los cambios.',
      );
    }
  };

  const saving = updateUser.isPending || setPassword.isPending;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-bold text-text">Editar usuario</h3>
            <p className="text-[12px] text-text-muted">{user.nombre}</p>
          </div>
          <button
            type="button"
            className="text-text-muted hover:text-text text-xl leading-none"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <form onSubmit={(e) => void submit(e)} className="p-5 flex flex-col gap-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="eu-nombre">Nombre completo</Label>
              <Input id="eu-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="eu-correo">Correo institucional</Label>
              <Input
                id="eu-correo"
                type="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eu-rol">Rol</Label>
              <select
                id="eu-rol"
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {roleList.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eu-estado">Estado</Label>
              <select
                id="eu-estado"
                value={estado}
                onChange={(e) => setEstado(e.target.value as UserStatus)}
                className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="activo">activo</option>
                <option value="inactivo">inactivo</option>
                <option value="bloqueado">bloqueado</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eu-esp">Especialidad</Label>
              <Input id="eu-esp" value={especialidad} onChange={(e) => setEspecialidad(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eu-rm">Registro profesional</Label>
              <Input
                id="eu-rm"
                value={registroProfesional}
                onChange={(e) => setRegistro(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eu-cc">Cédula</Label>
              <Input id="eu-cc" value={cedula} onChange={(e) => setCedula(e.target.value)} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-text-soft cursor-pointer select-none mt-1">
            <input
              type="checkbox"
              checked={puedeIniciarSesion}
              onChange={(e) => setPuedeIniciar(e.target.checked)}
              className="rounded border-border text-primary focus:ring-primary/20"
            />
            Puede iniciar sesión (cuenta de acceso)
          </label>

          <div className="border-t border-border pt-3 mt-1 flex flex-col gap-1.5">
            <Label htmlFor="eu-pwd" className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary" /> Cambiar contraseña
            </Label>
            <Input
              id="eu-pwd"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Dejar en blanco para no cambiarla"
              autoComplete="new-password"
            />
            <p className="text-[11px] text-text-muted">
              Mín. 12 caracteres con mayúscula, minúscula, dígito y carácter especial.
            </p>
          </div>

          {error !== null && (
            <div className="rounded-md bg-danger-soft border border-red-200 text-red-800 text-xs px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
